import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { ArrowRight, BookOpen, CornerDownLeft, History, Search, UserRound } from 'lucide-react';
import { cn } from '@/lib/cn';
import { usePermissions } from '@/lib/auth';
import { filterActions, filterNav, flattenNav, STAFF_ACTIONS, STAFF_NAV } from '@/lib/nav/staff';
import { rankBy } from '@/lib/search/fuzzy';
import { useGlobalSearch, useRecentCommands } from '@/lib/search/globalSearch';
import { Kbd } from '@/components/ui/misc';
import { Spinner } from '@/components/ui/Spinner';

interface Entry {
  key: string;
  section: string;
  label: string;
  detail?: string;
  icon: ReactNode;
  to: string;
}

const iconClass = 'h-4 w-4';

/**
 * Cmd/Ctrl+K palette: jump to any screen, run common actions, find patrons and catalogue records.
 * Prefix the query with ">" for actions only, "@" for patrons, "#" for catalogue items.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const navigate = useNavigate();
  const perms = usePermissions();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [recent, pushRecent] = useRecentCommands();

  const mode = query.startsWith('>') ? 'actions' : query.startsWith('@') ? 'patrons' : query.startsWith('#') ? 'items' : 'all';
  const term = mode === 'all' ? query : query.slice(1);
  const canPatrons = perms.has('borrowers');
  const canItems = perms.has('catalogue');
  const remote = useGlobalSearch(term, { patrons: canPatrons && (mode === 'all' || mode === 'patrons'), biblios: canItems && (mode === 'all' || mode === 'items') });

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) {
      return;
    }
    if (open && !dialog.open) {
      dialog.showModal();
      setQuery('');
      setActive(0);
      inputRef.current?.focus();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) {
      return;
    }
    const onCancel = (e: Event): void => {
      e.preventDefault();
      onClose();
    };
    const onDown = (e: MouseEvent): void => {
      if (e.target === dialog) {
        onClose();
      }
    };
    dialog.addEventListener('cancel', onCancel);
    dialog.addEventListener('mousedown', onDown);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
      dialog.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  const entries = useMemo<Entry[]>(() => {
    const nav = flattenNav(filterNav(STAFF_NAV, perms)).map<Entry>((n) => ({ key: `nav-${n.id}`, section: 'Go to', label: n.label, detail: n.parent ?? n.group, icon: <n.icon className={iconClass} aria-hidden="true" />, to: n.to }));
    const actions = filterActions(STAFF_ACTIONS, perms).map<Entry>((a) => ({ key: a.id, section: 'Actions', label: a.label, detail: a.shortcut, icon: <a.icon className={iconClass} aria-hidden="true" />, to: a.to }));
    const rank = <T extends { label: string }>(list: T[], limit: number, keywordsOf: (item: T) => readonly string[] = () => []): T[] => rankBy(list, term, (i) => ({ label: i.label, keywords: keywordsOf(i) }), limit);
    const navKeywords = new Map(flattenNav(filterNav(STAFF_NAV, perms)).map((n) => [`nav-${n.id}`, [...n.keywords, n.parent ?? '', n.group]]));
    const actionKeywords = new Map(STAFF_ACTIONS.map((a) => [a.id, a.keywords ?? []]));
    const patronEntries = remote.patrons.map<Entry>((p) => ({ key: `p-${p.id}`, section: 'Patrons', label: p.name, detail: p.detail, icon: <UserRound className={iconClass} aria-hidden="true" />, to: `/staff/patrons/${p.id}` }));
    const biblioEntries = remote.biblios.map<Entry>((b) => ({ key: `b-${b.id}`, section: 'Catalogue', label: b.title, detail: b.detail, icon: <BookOpen className={iconClass} aria-hidden="true" />, to: `/staff/catalog/${b.id}` }));

    if (mode === 'actions') {
      return rank(actions, 12, (a) => actionKeywords.get(a.key) ?? []);
    }
    if (mode === 'patrons' || mode === 'items') {
      const list = mode === 'patrons' ? patronEntries : biblioEntries;
      const fallback: Entry[] = term.trim() ? [{ key: 'search-all', section: mode === 'patrons' ? 'Patrons' : 'Catalogue', label: `Search ${mode === 'patrons' ? 'patrons' : 'the catalogue'} for "${term.trim()}"`, icon: <Search className={iconClass} aria-hidden="true" />, to: `${mode === 'patrons' ? '/staff/patrons' : '/staff/catalog'}?q=${encodeURIComponent(term.trim())}` }] : [];
      return [...list, ...fallback];
    }
    if (!term.trim()) {
      const recents = recent.filter((r) => nav.some((n) => n.to === r.to) || actions.some((a) => a.to === r.to) || r.to.startsWith('/staff')).map<Entry>((r) => ({ key: `recent-${r.id}`, section: 'Recent', label: r.label, icon: <History className={iconClass} aria-hidden="true" />, to: r.to }));
      return [...recents.slice(0, 4), ...actions.slice(0, 6)];
    }
    const matched = [...rank(actions, 4, (a) => actionKeywords.get(a.key) ?? []), ...patronEntries, ...biblioEntries, ...rank(nav, 6, (n) => navKeywords.get(n.key) ?? [])];
    const searches: Entry[] = [];
    if (canPatrons) {
      searches.push({ key: 'search-patrons', section: 'Search', label: `Find patrons matching "${term.trim()}"`, icon: <UserRound className={iconClass} aria-hidden="true" />, to: `/staff/patrons?q=${encodeURIComponent(term.trim())}` });
    }
    if (canItems) {
      searches.push({ key: 'search-items', section: 'Search', label: `Search the catalogue for "${term.trim()}"`, icon: <BookOpen className={iconClass} aria-hidden="true" />, to: `/staff/catalog?q=${encodeURIComponent(term.trim())}` });
    }
    return [...matched, ...searches];
  }, [perms, term, mode, remote.patrons, remote.biblios, recent, canPatrons, canItems]);

  const safeActive = Math.min(active, Math.max(0, entries.length - 1));

  const run = (entry: Entry): void => {
    pushRecent({ id: entry.key.replace(/^recent-/, ''), label: entry.label, to: entry.to });
    onClose();
    navigate(entry.to);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(Math.min(entries.length - 1, safeActive + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(Math.max(0, safeActive - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const entry = entries[safeActive];
      if (entry) {
        run(entry);
      }
    }
  };

  useEffect(() => {
    document.getElementById(`${listId}-${safeActive}`)?.scrollIntoView({ block: 'nearest' });
  }, [safeActive, listId]);

  // Section headings appear whenever the section changes between neighbours.
  let lastSection = '';

  return (
    <dialog ref={ref} aria-label="Command palette" className="m-auto mt-[12vh] w-[calc(100%-1.5rem)] max-w-2xl backdrop:bg-transparent">
      {open && (
        <div className="glass-strong animate-scale-in overflow-hidden rounded-2xl shadow-pop ring-1 ring-line">
          <div className="flex items-center gap-3 border-b border-line px-4">
            <Search className="h-5 w-5 shrink-0 text-ink-subtle" aria-hidden="true" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={entries.length ? `${listId}-${safeActive}` : undefined}
              aria-label="Search patrons, items, pages and actions"
              placeholder="Search patrons, items, pages and actions"
              autoComplete="off"
              spellCheck={false}
              className="h-14 flex-1 bg-transparent text-base text-ink outline-none placeholder:text-ink-subtle focus-visible:shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            {remote.loading && <Spinner size="sm" className="text-ink-subtle" label="Searching" />}
            <Kbd>Esc</Kbd>
          </div>
          <ul id={listId} role="listbox" aria-label="Results" className="max-h-[min(26rem,60vh)] overflow-auto p-2 scroll-thin">
            {entries.length === 0 && (
              <li role="presentation" className="px-3 py-10 text-center text-sm text-ink-muted">
                {term.trim().length < 2 && mode !== 'actions' ? 'Type to search' : 'No matches. Try another word.'}
              </li>
            )}
            {entries.map((entry, index) => {
              const heading = entry.section !== lastSection ? entry.section : null;
              lastSection = entry.section;
              const isActive = index === safeActive;
              return (
                <li key={entry.key} role="presentation">
                  {heading && <p className="px-3 pb-1 pt-3 text-2xs font-semibold uppercase tracking-wider text-ink-subtle first:pt-1">{heading}</p>}
                  <div
                    id={`${listId}-${index}`}
                    role="option"
                    aria-selected={isActive}
                    tabIndex={-1}
                    onMouseMove={() => setActive(index)}
                    onClick={() => run(entry)}
                    onKeyDown={(e) => e.key === 'Enter' && run(entry)}
                    className={cn('flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm', isActive ? 'bg-sapphire-50 text-sapphire-950 dark:bg-sapphire-900/50 dark:text-white' : 'text-ink')}
                  >
                    <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-lg', isActive ? 'bg-sapphire-600 text-white shadow-glow' : 'bg-sunken text-ink-muted')}>{entry.icon}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{entry.label}</span>
                      {entry.detail && <span className="block truncate text-xs text-ink-muted">{entry.detail}</span>}
                    </span>
                    {isActive && <ArrowRight className="h-4 w-4 shrink-0 text-sapphire-600 dark:text-sapphire-300" aria-hidden="true" />}
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line bg-sunken/60 px-4 py-2 text-xs text-ink-muted">
            <span className="flex items-center gap-1.5">
              <Kbd>&uarr;</Kbd>
              <Kbd>&darr;</Kbd> navigate
            </span>
            <span className="flex items-center gap-1.5">
              <Kbd>
                <CornerDownLeft className="h-3 w-3" aria-hidden="true" />
              </Kbd>
              open
            </span>
            <span className="ml-auto flex items-center gap-3">
              <span>
                <Kbd>&gt;</Kbd> actions
              </span>
              <span>
                <Kbd>@</Kbd> patrons
              </span>
              <span>
                <Kbd>#</Kbd> items
              </span>
            </span>
          </div>
        </div>
      )}
    </dialog>
  );
}
